import {
  CLASSIFIER_COMMAND,
  isGatewayAiCommand,
} from '../../utils/gateway-models';
import { agentOfName } from './agent-prompt';

/**
 * Which model each core agent's AI steps call through the AI Gateway, and
 * whether the Supervisor routes with OpenAI Decisions.
 *
 * A Supervisor on Decisions has two routing steps: a CallAIClassifier step
 * that picks the route, and a CallAIModel step its false / timeout / error
 * outcomes fall back to. Decisions is ON while the steps before the
 * Supervisor's routing lead into the classifier, and OFF when they lead
 * straight to the fallback model instead. Switching rewires those references
 * and nothing else, so it can be switched back. Pure: the panel applies the
 * plan.
 */

export interface ModelRow {
  id: string;
  nodeNumber?: number;
  nodeName?: unknown;
  command?: unknown;
  parameterInput?: unknown;
  whatsNext?: unknown;
  nextNodes?: unknown;
}

/** The agent's AI steps that go through the gateway, in table order. */
export const agentAiSteps = <R extends ModelRow>(
  rows: R[],
  agent: 'supervisor' | 'knowledge'
): R[] =>
  rows.filter(
    (r) =>
      isGatewayAiCommand(r.command) &&
      agentOfName(String(r.nodeName ?? '')) === agent
  );

/** A step's name without its solution prefix and command: "compose message". */
export const stepTitle = (row: ModelRow): string => {
  const name = String(row.nodeName ?? '').replace(/\s*\([^)]*\)\s*$/, '');
  const parts = name.split(/\s+[-–—]\s+/).map((p) => p.trim());
  const role = parts.findIndex((p) =>
    ['supervisor', 'knowledge'].includes(p.toLowerCase())
  );
  const rest = role >= 0 ? parts.slice(role + 1).join(' - ') : name;
  const title = rest || name;
  return title.charAt(0).toUpperCase() + title.slice(1);
};

const whatsNextOf = (row: ModelRow): string[] =>
  Array.isArray(row.whatsNext) ? (row.whatsNext as unknown[]).map(String) : [];

const nextNodesOf = (row: ModelRow): number[] =>
  Array.isArray(row.nextNodes) ? (row.nextNodes as unknown[]).map(Number) : [];

const routesTo = (row: ModelRow, target: number): boolean =>
  whatsNextOf(row).some((w) => Number(w.split('~')[1]) === target) ||
  nextNodesOf(row).includes(target);

export interface SupervisorRouting<R extends ModelRow> {
  classifier: R;
  fallback: R;
  /** True while the steps before routing lead into the classifier. */
  decisions: boolean;
}

/**
 * The Supervisor's Decisions routing, or null when it has none (no
 * classifier, or no model step the classifier falls back to).
 */
export const supervisorRouting = <R extends ModelRow>(
  rows: R[]
): SupervisorRouting<R> | null => {
  const steps = agentAiSteps(rows, 'supervisor');
  const classifier = steps.find((r) => r.command === CLASSIFIER_COMMAND);
  if (!classifier || classifier.nodeNumber === undefined) return null;
  const fallback = steps.find(
    (r) =>
      r.command !== CLASSIFIER_COMMAND &&
      r.nodeNumber !== undefined &&
      routesTo(classifier, r.nodeNumber)
  );
  if (!fallback) return null;
  const into = classifier.nodeNumber;
  return {
    classifier,
    fallback,
    decisions: rows.some((r) => r !== classifier && routesTo(r, into)),
  };
};

export interface RoutingUpdate {
  rowId: string;
  updates: { whatsNext?: string[]; nextNodes?: number[] };
}

/**
 * The edits that turn Decisions on or off: every step outside the
 * classifier that leads to one of the two routing steps leads to the other.
 * The classifier's own fallback routes stay as they are.
 */
export const planRouting = <R extends ModelRow>(
  rows: R[],
  routing: SupervisorRouting<R>,
  decisions: boolean
): RoutingUpdate[] => {
  const from = Number(
    (decisions ? routing.fallback : routing.classifier).nodeNumber
  );
  const to = Number(
    (decisions ? routing.classifier : routing.fallback).nodeNumber
  );
  return rows
    .filter((r) => r !== routing.classifier && r !== routing.fallback)
    .filter((r) => routesTo(r, from))
    .map((r) => {
      const updates: RoutingUpdate['updates'] = {};
      const wn = whatsNextOf(r);
      if (wn.some((w) => Number(w.split('~')[1]) === from)) {
        updates.whatsNext = wn.map((w) => {
          const [value, target] = w.split('~');
          return Number(target) === from ? `${value}~${to}` : w;
        });
      }
      const nn = nextNodesOf(r);
      if (nn.includes(from)) {
        updates.nextNodes = nn.map((n) => (n === from ? to : n));
      }
      return { rowId: r.id, updates };
    });
};
